import { useEffect, useMemo, useRef, useState } from "react"
import { CreateAreaSidebar } from "components/ui/customWebviewer/shared/CreateAreaSidebar/CreateAreaSidebar"
import { CategoriesSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/CategoriesSection/CategoriesSection"
import { CustomizationSection } from "components/ui/customWebviewer/shared/CustomizationSection/CustomizationSection"
import { useCustomizationFields } from "components/ui/customWebviewer/shared/CustomizationSection/useCustomizationFields.hook"
import { DimensionsSection } from "components/ui/customWebviewer/FurnitureSidebar/layout/DimensionsSection/DimensionsSection"
import { ElevationReferenceSelect } from "components/ui/customWebviewer/FurnitureSidebar/layout/ElevationReferenceSelect/ElevationReferenceSelect"
import { useCategorySelection } from "hooks/categorySelection.hook"
import type { TFurnitureCustomization, TPageElevationData } from "types/elevationReferences.type"
import style from "./CreateCountertopSidebar.module.scss"

const { fields, imagePlaceholder, divider } = style

type Props = {
	area: { pageNumber: number } | null
	isPending?: boolean
	onCancel: () => void
	onCreate: (values: {
		width: number
		height: number
		depth: number
		categoryId: number
		erefId: number | null
		erefVersion: number | null
		/**
		 * REQ-413/414. Null while the panel's Customization section is collapsed or incomplete,
		 * which is what keeps an untouched section from costing a request.
		 */
		customization: TFurnitureCustomization | null
	}) => void
	onClose: () => void
	relatedObjects?: TPageElevationData[]
}

/** Sidebar for a countertop rectangle that was drawn on the quotation canvas. */
export const CreateCountertopSidebar = ({
	area,
	isPending = false,
	onCancel,
	onCreate,
	onClose,
	relatedObjects = [],
}: Props) => {
	const [width, setWidth] = useState<number | null>(null)
	const [height, setHeight] = useState<number | null>(null)
	const [depth, setDepth] = useState<number | null>(null)
	const [erefId, setErefId] = useState<number | null>(null)
	const elevationReferenceRef = useRef<HTMLDivElement>(null)
	// REQ-413/414. Read once, at Create, like the rest of this form — the customization is applied
	// by a PUT chained onto the create, because its endpoint is per-object.
	const customization = useCustomizationFields()
	const {
		selectedTopLevelCategory,
		selectedSuperCategory,
		selectedModelCategory,
		selectedModelCategoryId,
		topLevelOptions,
		superCategoryOptions,
		modelCategoryOptions,
		handleTopLevelChange,
		handleSuperCategoryChange,
		handleModelCategoryChange,
	} = useCategorySelection({ defaultTopLevelCategory: "Countertops" })

	const erefs = useMemo(
		() => relatedObjects.find((page) => page.page_number === area?.pageNumber)?.erefs ?? [],
		[area?.pageNumber, relatedObjects],
	)
	const selectedEref = useMemo(() => erefs.find((eref) => eref.id === erefId) ?? null, [erefId, erefs])
	useEffect(() => {
		if (erefId !== null && selectedEref === null) setErefId(null)
	}, [erefId, selectedEref])
	const canCreate =
		area !== null &&
		width !== null &&
		width > 0 &&
		height !== null &&
		height > 0 &&
		depth !== null &&
		depth > 0 &&
		selectedModelCategoryId !== null &&
		!isPending

	return (
		<CreateAreaSidebar
			title="Object"
			hint="Drag on the drawing to define the object area"
			area={area}
			isPending={isPending}
			canSubmit={canCreate}
			submitLabel="Create object"
			onCancel={onCancel}
			onClose={onClose}
			onSubmit={() => {
				if (
					width === null ||
					width <= 0 ||
					height === null ||
					height <= 0 ||
					depth === null ||
					depth <= 0 ||
					selectedModelCategoryId === null
				)
					return
				onCreate({
					width,
					height,
					depth,
					categoryId: selectedModelCategoryId,
					erefId,
					erefVersion: selectedEref?.version ?? null,
					customization: customization.payload,
				})
			}}
			focusTargetRef={elevationReferenceRef}
		>
			<div className={fields}>
				<div className={imagePlaceholder}>
					Images are not supported
					<br />
					for countertop category
				</div>
				<div ref={elevationReferenceRef}>
					<ElevationReferenceSelect erefs={erefs} value={erefId} onChange={setErefId} required={false} />
				</div>
				<div className={divider} />
				<DimensionsSection
					width={width}
					height={height}
					depth={depth}
					min={1}
					onWidthChange={setWidth}
					onHeightChange={setHeight}
					onDepthChange={setDepth}
				/>
				<div className={divider} />
				<CustomizationSection
					isExpanded={customization.isExpanded}
					note={customization.note}
					priceAdjustment={customization.priceAdjustment}
					onExpand={customization.expand}
					onRemove={customization.collapse}
					onNoteChange={customization.setNote}
					onPriceAdjustmentChange={customization.setPriceAdjustment}
				/>
				<div className={divider} />
				<CategoriesSection
					topLevelValue={selectedTopLevelCategory}
					topLevelOptions={topLevelOptions}
					onTopLevelChange={handleTopLevelChange}
					superValue={selectedSuperCategory}
					superOptions={superCategoryOptions}
					onSuperChange={handleSuperCategoryChange}
					modelCategoryValue={selectedModelCategory}
					modelCategoryOptions={modelCategoryOptions}
					onModelCategoryChange={handleModelCategoryChange}
					topLevelDisabled
				/>
			</div>
		</CreateAreaSidebar>
	)
}
